/**
 * ARRANGEMENTETS FANER (QA15-S B5): Oversikt · Drift · Oppgaver og dokumenter · Advance · Innslag ·
 * Avtale · Chat — sju, uten sidelengs rulling på 1440.
 *
 * Drift er alt som skal skje på stedet: vakter, ressurser, akkreditering og gjestelisten. Oppgaver og
 * dokumenter er arbeidet før. Advance er produksjonen mot arrangøren og artistens advance. Innslag er
 * den eller de som opptrer (reisefølge, reise, overnatting). «Innslag» og ikke «Artist»: på
 * arrangementet kan det være et kor, et foredrag eller en organisasjon (B1); i Booking heter det artist.
 *
 * `?tab=` i adressen, og de gamle fane-id-ene (vakter, ressurser, kontrakt …) lander i fanen de nå
 * står i, så en lenke fra et varsel eller en vaktliste ikke faller til Oversikt.
 *
 * Cellene er de fanene selv spør om (planen §2): en fane leseren ikke har, er borte — ikke grå.
 */
import type { Action, VenueNightDetailDto } from '@tvitvi/shared';

import { validateAccessSearch, type AccessSearch } from '@/features/advance-access/access-search';
import type { TKey } from '@/i18n/types';

export interface EventTabProps {
  night: VenueNightDetailDto;
  organizationId: string;
  festivalId: string;
  /**
   * Innslaget de artistbundne fanene og Tilgang-skuffen gjelder, når det er flere (B1). Valget står
   * på siden, så headerens tilgangsknapp og Innslag-fanens velger alltid peker på samme programpost.
   */
  performerChoice?: { id: string; choose: (performanceId: string) => void };
}

export interface EventTabDef {
  id: string;
  labelKey: TKey;
  /** Cellene fanen leser med; én av dem holder. Utelatt: arrangementets egen (`venue_night:read`). */
  anyOf?: readonly Action[];
  /** Bare når arrangementet har et innslag (en programpost). */
  needsPerformance?: true;
}

export const EVENT_TABS = [
  { id: 'overview', labelKey: 'venue.eventTabs.overview' },
  { id: 'operations', labelKey: 'venue.eventTabs.operations' },
  { id: 'work', labelKey: 'venue.eventTabs.work', anyOf: ['task:list', 'document:list'] },
  { id: 'advance', labelKey: 'venue.eventTabs.advance' },
  {
    id: 'performer',
    labelKey: 'venue.eventTabs.performer',
    anyOf: ['performance:read'],
    needsPerformance: true,
  },
  {
    id: 'deal',
    labelKey: 'venue.eventTabs.deal',
    anyOf: ['contract:read'],
    needsPerformance: true,
  },
  { id: 'chat', labelKey: 'chat.tab', anyOf: ['performance:read'], needsPerformance: true },
] as const satisfies readonly EventTabDef[];

export type EventTabId = (typeof EVENT_TABS)[number]['id'];
export const EVENT_TAB_IDS: readonly EventTabId[] = EVENT_TABS.map((tab) => tab.id);

/** De gamle fanene (sak 229/230) og hvor de står nå. */
const LEGACY_TABS: Readonly<Record<string, { tab: EventTabId; section?: string }>> = {
  shifts: { tab: 'operations', section: 'shifts' },
  resources: { tab: 'operations', section: 'resources' },
  accreditation: { tab: 'operations', section: 'accreditation' },
  tasks: { tab: 'work', section: 'tasks' },
  documents: { tab: 'work', section: 'documents' },
  artist: { tab: 'performer', section: 'people' },
  travel: { tab: 'performer', section: 'travel' },
  stay: { tab: 'performer', section: 'stay' },
  artistAdvance: { tab: 'advance', section: 'artist' },
  contract: { tab: 'deal' },
};

export interface EventSearch extends AccessSearch {
  tab?: EventTabId;
  /** Seksjonen i fanen (Drift: vakter, ressurser …), fra en gammel lenke eller et varsel. */
  section?: string;
}

/** Tilgang-skuffen fra en lenke (`?access=1&accessPerson=`) står ved siden av fanen. */
export function validateEventSearch(search: Record<string, unknown>): EventSearch {
  return { ...validateEventTab(search), ...validateAccessSearch(search) };
}

function validateEventTab(search: Record<string, unknown>): EventSearch {
  const tab = search['tab'];
  const section = typeof search['section'] === 'string' ? search['section'] : undefined;
  if (typeof tab !== 'string') return {};
  if ((EVENT_TAB_IDS as readonly string[]).includes(tab)) {
    return { tab: tab as EventTabId, ...(section === undefined ? {} : { section }) };
  }
  const legacy = LEGACY_TABS[tab];
  if (legacy === undefined) return {};
  const landed = section ?? legacy.section;
  return { tab: legacy.tab, ...(landed === undefined ? {} : { section: landed }) };
}
